// ---------------------------------------------------------------------------
// useUserBi — typed wrapper over the business-analytics (userbi) telemetry API.
//
// The TSL `userbi` API takes freeform strings for panelType, panelRegion,
// actionScenario and actionScenarioType. This hook constrains them to the
// registered unions (see src/telemetry/userbi.ts) and maps the app-friendly
// option shapes onto the SDK's `PanelActionEvent` / `PanelViewEvent`.
//
// No-op-safe: every method is a no-op when telemetry is disabled.
// ---------------------------------------------------------------------------

import type {
  PanelActionEvent,
  PanelViewEvent,
} from '@teamstelemetry/shared-logger';
import { useCallback, useMemo } from 'react';

import { AppError } from '../../../api/errors.js';
import { useOptionalAppContext } from '../../../context/AppContext.js';
import {
  ActionOutcome,
  getOperationActionScenario,
  getOperationScenarioName,
  PanelRegion,
} from '../../userbi.js';

import type {
  UseUserBiReturn,
  UserBiActionOptions,
  UserBiData,
  UserBiOperationOptions,
  UserBiViewOptions,
} from './useUserBi.interface.js';

function toViewEvent(options: UserBiViewOptions): PanelViewEvent {
  return {
    panelType: options.panelType,
    panelRegion: options.panelRegion,
    eventID: options.eventID,
    moduleName: options.moduleName,
    moduleType: options.moduleType,
  };
}

function toActionEvent(options: UserBiActionOptions): PanelActionEvent {
  return {
    ...toViewEvent(options),
    actionGesture: options.actionGesture ?? 'auto',
    actionOutcome: options.actionOutcome ?? ActionOutcome.Success,
    actionScenario: options.actionScenario,
    actionScenarioType: options.actionScenarioType,
  };
}

export function useUserBi(): UseUserBiReturn {
  const appContext = useOptionalAppContext();
  const biLogger = appContext?.biLogger;
  const scenarioFactory = appContext?.scenarioFactory;

  const recordAction = useCallback(
    (options: UserBiActionOptions) => {
      biLogger?.userbi.recordAction(toActionEvent(options), options.data);
    },
    [biLogger],
  );

  const recordView = useCallback(
    (options: UserBiViewOptions) => {
      biLogger?.userbi.recordView(toViewEvent(options), options.data);
    },
    [biLogger],
  );

  const trackOperation = useCallback(
    async <T>(
      options: UserBiOperationOptions,
      operation: (correlationId: string) => Promise<T>,
    ): Promise<T> => {
      const actionScenario = getOperationActionScenario(
        options.entityType,
        options.operation,
      );
      const data: UserBiData = {
        'Usage.EntityType': options.entityType,
        'Usage.Operation': options.operation,
        ...options.data,
      };

      biLogger?.userbi.recordAction(
        toActionEvent({
          panelType: options.panelType,
          panelRegion: options.panelRegion ?? PanelRegion.Main,
          eventID: actionScenario,
          actionGesture: options.actionGesture ?? 'click',
          actionOutcome: ActionOutcome.Requested,
          actionScenario,
          actionScenarioType: options.operation,
        }),
        data,
      );

      const scenario = scenarioFactory?.newScenario(
        options.scenarioName ??
          getOperationScenarioName(options.entityType, options.operation),
      );

      // Surface the scenario's correlation GUID so the wrapped operation can
      // carry it to TEDH (via extras.correlationId). Empty string when
      // telemetry is disabled — callers must omit it in that case.
      const correlationId = scenario?.queryId ?? '';

      try {
        const result = await operation(correlationId);
        scenario?.complete('success', data);
        return result;
      } catch (error) {
        const errorCategory = getErrorCategory(error);
        scenario?.complete(
          errorCategory === 'cancelled' ? 'abandoned' : 'failure',
          {
            ...data,
            'Usage.ErrorCategory': errorCategory,
          },
        );
        throw error;
      }
    },
    [biLogger, scenarioFactory],
  );

  return useMemo(
    () => ({ recordAction, recordView, trackOperation }),
    [recordAction, recordView, trackOperation],
  );
}

function getErrorCategory(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'cancelled';
  }
  if (error instanceof AppError) {
    if (error.code === 'NETWORK_ERROR') return 'network';
    const status = error.statusCode;
    if (status === 401 || status === 403) return 'authorization';
    if (status === 404) return 'not_found';
    if (status === 409) return 'conflict';
    if (status === 429) return 'throttled';
    if (status !== undefined && status >= 500) return 'server';
    if (status !== undefined && status >= 400) return 'validation';
  }
  return 'unknown';
}
